import type {
  StoreDayOpeningTimeFragment,
  StoreOpeningTimesFragment,
} from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { StoreOpeningTimeRangeLabel } from '@shared/gql/document-nodes'
import { Tab, TabPane } from '@sok/design-system'
import { getDateTimeNow } from 'domain/date-time'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'

import { StoreInfoCard, StoreInfoCardItemWrapper, StoreInfoCardTitle } from '../StoreInfoCard'
import { TodaysOpeningTime } from './TodayOpeningTime'
import { WeekOpeningTimes } from './WeekOpeningTimes'

export const useTodaysOpeningTime = <T extends Pick<StoreDayOpeningTimeFragment, 'date'>>(
  openingTimes: T[],
): T | undefined => {
  const today = getDateTimeNow().toISODate()
  const todaysOpeningTime = openingTimes.find((openingTime) => openingTime.date === today)

  return todaysOpeningTime
}

export const useOpeningTime = () => {
  const { t } = useTranslation()

  const getOpeningTime = useCallback(
    <T extends Pick<StoreDayOpeningTimeFragment, 'mode' | 'ranges'>>(openingTime: T) => {
      switch (openingTime.mode) {
        case StoreOpeningTimeRangeLabel.Range:
          return openingTime.ranges?.map((range) => `${range.open}—${range.close}`).join(' ')
        case StoreOpeningTimeRangeLabel.AllDay:
          return t('24h')
        case StoreOpeningTimeRangeLabel.Closed:
          return t('Closed')
      }
    },
    [t],
  )

  return getOpeningTime
}

export type StoreOpeningTimesProps = {
  store: StoreOpeningTimesFragment
}

export const StoreOpeningTimes: FC<StoreOpeningTimesProps> = ({ store }) => {
  const { t } = useTranslation()

  const currentWeekOpeningTimes = store.weeklyOpeningHours?.[0]
  const todaysOpeningTime = useTodaysOpeningTime(currentWeekOpeningTimes?.openingTimes ?? [])

  const isClientSide = useIsClientSide()

  if (!store.weeklyOpeningHours) {
    return null
  }

  const weekTitle = (i: number, weekNumber: number) => {
    return i === 0 ? t('This week') : `${t('Week')} ${weekNumber}`
  }

  return (
    <StoreInfoCard>
      <StoreInfoCardTitle>{t('Opening times')}</StoreInfoCardTitle>

      <StoreInfoCardItemWrapper data-test-id="today-opening-hours">
        <TodaysOpeningTime openingTime={todaysOpeningTime} />
      </StoreInfoCardItemWrapper>

      {/* Tab component causes hydration errors */}
      {isClientSide && (
        <StoreInfoCardItemWrapper data-test-id="week-opening-hours">
          <Tab disableLine>
            {store.weeklyOpeningHours.map((weekOpeningTimes, i) => (
              <TabPane tab={weekTitle(i, weekOpeningTimes.weekNumber)} key={i}>
                <WeekOpeningTimes weekOpeningTimes={weekOpeningTimes.openingTimes} />
              </TabPane>
            ))}
          </Tab>
        </StoreInfoCardItemWrapper>
      )}
    </StoreInfoCard>
  )
}
